import {
  Stack,
  Text,
  Icon,
  Label,
  Link,
  Panel,
  PanelHeader,
  PanelTitle,
  PanelSection,
  InlineStack,
  StatusIndicator,
} from '@customerio/pluma-components/react';
import type { ServicesApiEnvHealthAlert } from '@/services/api/services-api';
import { SEVERITY_COLOR } from '../utils';

function collapseProcessingAlerts(alerts: ServicesApiEnvHealthAlert[]): ServicesApiEnvHealthAlert[] {
  const processingAlerts = alerts.filter((a) => a.action_type === 'processing_details');
  const otherAlerts = alerts.filter((a) => a.action_type !== 'processing_details');

  if (processingAlerts.length <= 2) {
    return alerts;
  }

  const highestSeverity = processingAlerts.some((a) => a.severity === 'error') ? 'error' : 'caution';

  const collapsed: ServicesApiEnvHealthAlert = {
    severity: highestSeverity,
    message: 'Multiple processing queues are behind — some data and messages may be delayed',
    action_label: 'View details',
    action_type: 'processing_details',
  };

  return [collapsed, ...otherAlerts];
}

interface AlertsPanelProps {
  alerts: ServicesApiEnvHealthAlert[];
  onOpenBreakdown: () => void;
  workspaceId: number;
}

export function AlertsPanel({ alerts: rawAlerts, onOpenBreakdown, workspaceId }: AlertsPanelProps) {
  const alerts = collapseProcessingAlerts(rawAlerts);
  const hasAlerts = alerts.length > 0;
  const badgeColor = alerts.some((a) => a.severity === 'error') ? 'red' : 'yellow';

  const actionProps = (alert: ServicesApiEnvHealthAlert) => {
    switch (alert.action_type) {
      case 'processing_details':
        return { onClick: onOpenBreakdown } as const;
      case 'tasks':
        return { href: `/workspaces/${workspaceId}/tasks` } as const;
      case 'imports':
        return { href: `/workspaces/${workspaceId}/journeys/imports` } as const;
      default:
        return { onClick: onOpenBreakdown } as const;
    }
  };

  return (
    <Panel>
      <PanelHeader>
        <PanelTitle text="product-h4" color="subtle">
          <InlineStack gap="050" alignBlock="center">
            <Icon name="notifications" size="sm" color="bold" />
            Alerts
            {hasAlerts ? (
              <Label color={badgeColor} showStatusIndicator>
                {alerts.length}
              </Label>
            ) : null}
          </InlineStack>
        </PanelTitle>
      </PanelHeader>
      <PanelSection>
        {hasAlerts ? (
          <Stack gap="150">
            {alerts.map((alert, i) => {
              const props = actionProps(alert);
              return (
                <InlineStack key={i} gap="100" alignBlock="center">
                  <StatusIndicator color={SEVERITY_COLOR[alert.severity]} />
                  <Text size="s">{alert.message}</Text>
                  <Link variant="secondary" {...props}>
                    <Text size="s">{alert.action_label}</Text>
                  </Link>
                </InlineStack>
              );
            })}
          </Stack>
        ) : (
          <InlineStack gap="100" alignBlock="center">
            <Icon name="check-circle" size="sm" color="success" />
            <Text size="s" color="subtle">
              All clear — nothing needs your attention
            </Text>
          </InlineStack>
        )}
      </PanelSection>
    </Panel>
  );
}
